import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getGuests, adminLogin } from '../../data/api';
import DashboardTab from './DashboardTab';
import { AdminToaster } from './AdminToaster';

const API = process.env.REACT_APP_BACKEND_URL;
const HOST_DASHBOARD_CONTENT_MIN_WIDTH = 1680;
const HOST_DASHBOARD_CONTENT_PADDING = 20;
const HOST_DASHBOARD_CANVAS_MIN_WIDTH =
  HOST_DASHBOARD_CONTENT_MIN_WIDTH + HOST_DASHBOARD_CONTENT_PADDING * 2;

/**
 * HostDashboardPage
 * ---------------------------------------------------------------------------
 * Additive read-only entry point that renders the existing <DashboardTab/>
 * with live data, on a separate URL (/admin/host-dashboard). Designed for
 * the host to check KPIs from a phone without opening the full admin shell.
 *
 * Strict scope (do not break):
 *   - Does NOT modify AdminPage.jsx, DashboardTab.jsx, or any backend.
 *   - Re-uses existing admin session (sessionStorage 'admin_authed').
 *   - Duplicates the data fetch + prop bundle from AdminPage.jsx by design
 *     (option a2 chosen by the host pre-event — refactor deferred).
 *   - Cross-tab navigation from inside DashboardTab is redirected to /admin
 *     because this page intentionally has no sidebar / no tab shell.
 *
 * Drift checklist — if any of these change in AdminPage.refreshAll()
 * or AdminPage's <DashboardTab/> prop bundle (lines ~638-697 / ~2055-2099),
 * mirror them here verbatim:
 *   - endpoint list in fetchData()
 *   - dashboardBudget computation
 *   - liquidity computation
 *   - DashboardTab prop names + defaults
 */
const HostDashboardPage = () => {
  const navigate = useNavigate();
  const [authed, setAuthed] = useState(!!sessionStorage.getItem('admin_authed'));
  const [pw, setPw] = useState('');
  const [pwErr, setPwErr] = useState('');
  const [loggingIn, setLoggingIn] = useState(false);

  const handleInlineLogin = async () => {
    if (!pw || loggingIn) return;
    setLoggingIn(true);
    setPwErr('');
    try {
      const r = await adminLogin(pw);
      if (r.success) {
        sessionStorage.setItem('admin_authed', '1');
        if (r.token) sessionStorage.setItem('admin_token', r.token);
        setAuthed(true);
      } else {
        setPwErr(r.message || 'Invalid password');
      }
    } catch (e) {
      console.error('Host dashboard login error:', e);
      setPwErr('Error signing in. Please try again.');
    } finally {
      setLoggingIn(false);
    }
  };

  // ── State (mirrors AdminPage.jsx) ──────────────────────────────────────────
  const [guests, setGuests] = useState([]);
  const [dashStats, setDashStats] = useState(null);
  const [dashStatsCheckedAt, setDashStatsCheckedAt] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [messages, setMessages] = useState([]);
  const [financeEntries, setFinanceEntries] = useState([]);
  const [sponsors, setSponsors] = useState([]);
  const [dashboardBudget, setDashboardBudget] = useState(null);
  const [officialInvitationsSent, setOfficialInvitationsSent] = useState(0);
  const [eventConfig, setEventConfig] = useState({
    name: '',
    honoree: '',
    date: '2026-07-12',
    location: '',
    capacity: null,
    minimum_guest_target: null,
  });
  const [dashboardTitle, setDashboardTitle] = useState('80 Years of Connection');
  const [loading, setLoading] = useState(true);

  // ── Data fetch (mirrors AdminPage.refreshAll) ──────────────────────────────
  const fetchData = useCallback(async () => {
    if (!authed) return;
    try {
      const [gr, vr, mr, br, fer, spr, dashStatsRes] = await Promise.all([
        getGuests(),
        axios.get(`${API}/api/vendors`).catch(() => ({ data: [] })),
        axios.get(`${API}/api/messages`).catch(() => ({ data: [] })),
        axios.get(`${API}/api/budget`).catch(() => ({ data: null })),
        axios.get(`${API}/api/finance/entries`).catch(() => ({ data: [] })),
        axios.get(`${API}/api/sponsors`).catch(() => ({ data: [] })),
        axios.get(`${API}/api/dashboard/stats`).catch(() => ({ data: {} })),
      ]);

      setGuests(gr || []);
      setDashStats(dashStatsRes.data);
      setDashStatsCheckedAt(new Date());
      setVendors(vr.data || []);
      setMessages(mr.data || []);
      setFinanceEntries(fer.data || []);
      setOfficialInvitationsSent(dashStatsRes.data?.official_invitations_sent ?? 0);

      // dashboardBudget — same derivation as AdminPage.refreshAll
      const budgetData = br.data;
      const financeEntriesData = fer.data || [];
      const sponsorsData = spr.data || [];
      const vendorsData = vr.data || [];
      setSponsors(sponsorsData);

      if (budgetData) {
        const totalBudget = budgetData.total_budget || 0;
        const reservePercent = budgetData.reserve_percent || 10;
        const reserveAmount = totalBudget * (reservePercent / 100);
        const financeSpent = financeEntriesData
          .filter(e => !e.cancelled)
          .reduce((sum, e) => sum + (e.amount_paid || 0), 0);
        const vendorSpent = vendorsData.reduce((sum, v) => sum + (v.paid_to_date || 0), 0);
        const totalSpent = financeSpent + vendorSpent;
        const availableBudget = totalBudget - reserveAmount - totalSpent;
        setDashboardBudget({
          total_budget: totalBudget,
          spent_budget: totalSpent,
          reserve_amount: reserveAmount,
          available_budget: availableBudget,
        });
      } else {
        setDashboardBudget(null);
      }
    } catch (e) {
      console.error('HostDashboard fetch error:', e);
    } finally {
      setLoading(false);
    }
  }, [authed]);

  // ── Event config (mirrors AdminPage useEffect) ─────────────────────────────
  useEffect(() => {
    const fetchEventConfig = async () => {
      try {
        const res = await axios.get(`${API}/api/event/config`);
        if (res.data) {
          setEventConfig(res.data);
          if (res.data.name) setDashboardTitle(res.data.name);
        }
      } catch (e) {
        console.error('HostDashboard event config error:', e);
      }
    };
    if (authed) fetchEventConfig();
  }, [authed]);

  useEffect(() => {
    if (authed) fetchData();
  }, [authed, fetchData]);

  // ── Auth gate — inline login (no redirect to /admin on mobile) ─────────────
  // Phones hit the legacy dark mobile branch on /admin, which strands the
  // user. Inline login keeps everything on this URL so signing in renders
  // the desktop dashboard immediately.
  if (!authed) {
    return (
      <div data-testid="host-dashboard-login" style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#F7F3EC',
        fontFamily: 'system-ui, -apple-system, sans-serif',
        padding: 24,
      }}>
        <div style={{
          background: '#FFFFFF',
          borderRadius: 12,
          border: '1px solid rgba(185, 139, 31, 0.25)',
          boxShadow: '0 10px 40px rgba(0,0,0,0.08)',
          padding: '36px 28px',
          maxWidth: 420,
          width: '100%',
          textAlign: 'center',
          boxSizing: 'border-box',
        }}>
          <h2 style={{
            fontFamily: 'Georgia, "Times New Roman", serif',
            fontSize: 24,
            color: '#1B1812',
            marginTop: 0,
            marginBottom: 8,
          }}>Host Dashboard</h2>
          <p style={{ fontSize: 13, color: '#5b5447', margin: '0 0 24px', lineHeight: 1.5 }}>
            Enter the admin password to view the live dashboard.
          </p>
          {pwErr && (
            <div data-testid="host-dashboard-login-err" style={{
              background: 'rgba(220,53,69,0.08)',
              border: '1px solid rgba(220,53,69,0.25)',
              borderRadius: 8,
              padding: '10px 14px',
              marginBottom: 16,
              fontSize: 13,
              color: '#a01724',
              textAlign: 'left',
            }}>{pwErr}</div>
          )}
          <input
            data-testid="host-dashboard-password-input"
            type="password"
            placeholder="Password"
            autoFocus
            value={pw}
            onChange={e => { setPw(e.target.value); setPwErr(''); }}
            onKeyDown={e => e.key === 'Enter' && handleInlineLogin()}
            style={{
              width: '100%',
              padding: '14px 16px',
              borderRadius: 8,
              border: '1px solid rgba(185, 139, 31, 0.30)',
              background: '#FCFAF5',
              color: '#1B1812',
              fontSize: 16,
              marginBottom: 16,
              outline: 'none',
              boxSizing: 'border-box',
            }}
          />
          <button
            data-testid="host-dashboard-login-btn"
            onClick={handleInlineLogin}
            disabled={loggingIn || !pw}
            style={{
              width: '100%',
              padding: '14px',
              borderRadius: 8,
              background: loggingIn || !pw
                ? 'linear-gradient(135deg, #ddd2b3 0%, #b9a371 100%)'
                : 'linear-gradient(135deg, #f8e7a0 0%, #b98b1f 100%)',
              color: '#1B1812',
              border: 'none',
              fontSize: 15,
              fontWeight: 700,
              cursor: loggingIn || !pw ? 'not-allowed' : 'pointer',
              letterSpacing: '0.3px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.10)',
            }}
          >
            {loggingIn ? 'Signing in…' : 'Sign in'}
          </button>
        </div>
      </div>
    );
  }

  // ── DashboardTab prop bundle (mirrors AdminPage.jsx lines ~2055-2099) ──────
  // PROTECTED KPI CONSUMER — values come straight from /api/dashboard/stats.
  // Do NOT re-derive these from the client-side `guests` array.
  const _rsvpStatus = dashStats?.guest_statistics?.rsvp_status || {};
  const _attending = dashStats?.guest_statistics?.attending_breakdown || {};
  const rsvpStats = {
    total_invited: _rsvpStatus.total_invited ?? guests.length,
    yes: _rsvpStatus.yes ?? 0,
    no: _rsvpStatus.no ?? 0,
    pending: _rsvpStatus.pending ?? 0,
    adults_attending: _attending.adults ?? 0,
    children_attending: _attending.children ?? 0,
    unknown_age_attending: _attending.unknown_age ?? 0,
    total_attending: _attending.total_attending ?? 0,
    integrity_check: dashStats?.guest_statistics?.integrity_check ?? { passed: true, failures: [] },
    integrity_checked_at: dashStatsCheckedAt,
    drift_check: dashStats?.guest_statistics?.drift_check ?? { has_drift: false, offenders: [] },
    invite_funnel: dashStats?.invite_funnel ?? null,
  };

  const guestStatsProp = {
    total: rsvpStats.total_invited,
    pending: rsvpStats.pending,
    sent: guests.filter(g => g.invitation_status === 'sent').length || 0,
    failed: guests.filter(g => g.invitation_status === 'failed').length || 0,
    rsvp_yes: rsvpStats.yes,
    rsvp_no: rsvpStats.no,
    adults_attending: rsvpStats.adults_attending,
    children_attending: rsvpStats.children_attending,
    unknown_age_attending: rsvpStats.unknown_age_attending,
    total_attending: rsvpStats.total_attending,
    adults_yes: rsvpStats.adults_attending,
    children_yes: rsvpStats.children_attending,
    official_invitations_sent: officialInvitationsSent,
    integrity_check: rsvpStats.integrity_check,
    integrity_checked_at: rsvpStats.integrity_checked_at,
    drift_check: rsvpStats.drift_check,
    invite_funnel: rsvpStats.invite_funnel,
  };

  const liquidity = (() => {
    const cashReceived = sponsors.reduce((sum, s) => sum + (s.amount_received || s.received || 0), 0);
    const vendorCommitted = vendors.reduce((sum, v) => sum + (v.contract_total || 0), 0);
    const expenseCommitted = financeEntries
      .filter(e => e.type !== 'sponsor' && !e.cancelled)
      .reduce((sum, e) => sum + (e.contract_amount || 0), 0);
    return { cash_received: cashReceived, committed: vendorCommitted + expenseCommitted };
  })();

  const eventConfigProp = {
    id: '1',
    name: dashboardTitle,
    date: eventConfig.date,
    location: eventConfig.location || 'Passaic, NJ',
    honoree: eventConfig.honoree || 'Our Beloved',
    minimum_guest_target: eventConfig.minimum_guest_target,
    adult_plate_cost: eventConfig.adult_plate_cost,
    child_plate_cost: eventConfig.child_plate_cost,
  };

  // Cross-tab navigation hand-off: this page has no sidebar; redirect to /admin.
  const handleNavigate = (_tab) => {
    navigate('/admin');
  };

  return (
    <div
      data-testid="host-dashboard-page"
      style={{
        minHeight: '100vh',
        width: '100%',
        minWidth: HOST_DASHBOARD_CANVAS_MIN_WIDTH,
        // Clean solid cream — no silk image, no fixed attachment.
        // The admin-bg silk is used by guest-facing pages and bleeds through
        // wherever desktop cards don't extend on a phone viewport. A flat
        // background keeps the dashboard the focus and avoids the "two
        // layers detaching" feeling reported during vertical scroll.
        backgroundColor: '#F7F3EC',
      }}
    >
      {/* Slim header — purely informational, mirrors admin gold-accent style. */}
      <header
        data-testid="host-dashboard-header"
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 50,
          background: '#000000',
          padding: '12px 20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          boxShadow: '0 4px 16px rgba(0,0,0,0.25)',
        }}
      >
        <div style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: 2,
          background: 'linear-gradient(90deg, transparent 0%, #6B5018 8%, #C6A048 30%, #F0D080 50%, #C6A048 70%, #6B5018 92%, transparent 100%)',
          pointerEvents: 'none',
        }} />
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, minWidth: 0 }}>
          <span style={{
            fontFamily: 'Georgia, "Times New Roman", serif',
            fontStyle: 'italic',
            fontSize: 18,
            background: 'linear-gradient(135deg, #f8e7a0 0%, #b98b1f 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            whiteSpace: 'nowrap',
          }}>Host View</span>
          <span style={{
            fontSize: 12,
            color: 'rgba(255,255,255,0.55)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}>{dashboardTitle}</span>
        </div>
        <button
          data-testid="host-dashboard-back-to-admin"
          onClick={() => navigate('/admin')}
          style={{
            padding: '6px 14px',
            borderRadius: 8,
            background: 'rgba(198, 160, 72, 0.15)',
            border: '1px solid rgba(198, 160, 72, 0.45)',
            color: '#f8e7a0',
            fontSize: 12,
            fontWeight: 600,
            cursor: 'pointer',
            whiteSpace: 'nowrap',
          }}
        >
          Open Full Admin
        </button>
      </header>

      {/* Dashboard content — the existing <DashboardTab/>, unmodified. */}
      <div
        data-testid="host-dashboard-content"
        style={{
          padding: HOST_DASHBOARD_CONTENT_PADDING,
          // Match AdminPage's desktop content min-width so DashboardTab renders
          // exactly as it does on /admin. On narrow viewports the page will
          // scroll horizontally — accepted by the host (option a2 / b1).
          minWidth: HOST_DASHBOARD_CONTENT_MIN_WIDTH,
        }}
      >
        {loading && !dashStats ? (
          <div style={{
            padding: 60,
            textAlign: 'center',
            color: '#5b5447',
            fontSize: 14,
          }} data-testid="host-dashboard-loading">
            Loading dashboard…
          </div>
        ) : (
          <DashboardTab
            guestStats={guestStatsProp}
            messages={messages}
            onDeleteMessage={(id) => setMessages(prev => prev.filter(m => m.id !== id))}
            vendors={vendors}
            budget={dashboardBudget}
            liquidity={liquidity}
            eventConfig={eventConfigProp}
            onNavigate={handleNavigate}
          />
        )}
      </div>

      <AdminToaster />
    </div>
  );
};

export default HostDashboardPage;
